/**
 * Modal interno "+ NUEVA PLAYLIST".
 *
 * Flujo:
 *  - openNewPlaylistModal() → resetea pendingCoverId, llena el picker,
 *    muestra el modal, focus en el input.
 *  - El usuario escribe nombre y elige una cover (toggle de selección
 *    se gestiona vía callback de renderCoverPicker).
 *  - createPlaylist() → valida, crea, persiste, cierra el modal.
 *  - closeNewPlaylistModal() → oculta el modal.
 */

import { AVAILABLE_COVERS } from './data.js';
import { hashName } from './helpers.js';
import { savePlaylists } from './persistence.js';
import { renderCoverPicker, renderFileList, renderFolderList, renderPlaylists } from './render.js';
import { getCallbacks } from './controls.js';
import { setActivePlaylist, setPlaylists, state } from './state.js';
import type { Playlist } from './types.js';

/** Cover seleccionada actualmente en el modal de creación. */
let pendingCoverId: string = AVAILABLE_COVERS[0]!.id;

/** Abre el modal y rellena el picker de covers con la primera seleccionada. */
export function openNewPlaylistModal(): void {
  pendingCoverId = AVAILABLE_COVERS[0]!.id;
  renderCoverPickerWithSelection();

  const inp = document.getElementById('r-newpl-name') as HTMLInputElement | null;
  if (inp) {
    inp.value = '';
    window.setTimeout(() => inp.focus(), 50);
  }

  document.getElementById('r-newpl-modal')?.classList.add('r-show');
}

/**
 * Re-renderiza el picker de covers manteniendo `pendingCoverId` como
 * seleccionado. Click en una cover actualiza pendingCoverId y vuelve a
 * renderizar para mostrar el cambio visual.
 */
function renderCoverPickerWithSelection(): void {
  renderCoverPicker(pendingCoverId, (cid: string) => {
    pendingCoverId = cid;
    renderCoverPickerWithSelection();
  });
}

/** Cierra el modal sin crear nada. */
export function closeNewPlaylistModal(): void {
  document.getElementById('r-newpl-modal')?.classList.remove('r-show');
}

/** Crea la playlist con el nombre+cover actuales y la activa. */
export function createPlaylist(): void {
  const inp = document.getElementById('r-newpl-name') as HTMLInputElement | null;
  const name = (inp?.value ?? '').trim();
  if (!name) {
    inp?.focus();
    return;
  }

  const id = 'pl_' + Date.now().toString(36) + '_' + hashName(name);
  const newPl: Playlist = {
    id,
    name: name.toUpperCase(),
    coverId: pendingCoverId,
    tracks: [],
  };

  setPlaylists([...state.playlists, newPl]);
  setActivePlaylist(id);

  savePlaylists();
  renderPlaylists(getCallbacks());
  renderFileList(getCallbacks());
  renderFolderList(getCallbacks());
  closeNewPlaylistModal();
}

/** Conecta los listeners del modal de creación. Idempotente. */
export function initNewPlaylistModal(): void {
  const newBtn = document.getElementById('r-new-pl-btn');
  const cancelBtn = document.getElementById('r-newpl-cancel');
  const createBtn = document.getElementById('r-newpl-create');
  const nameInp = document.getElementById('r-newpl-name') as HTMLInputElement | null;

  if (newBtn && newBtn.dataset.bound !== '1') {
    newBtn.dataset.bound = '1';
    newBtn.addEventListener('click', openNewPlaylistModal);
  }
  if (cancelBtn && cancelBtn.dataset.bound !== '1') {
    cancelBtn.dataset.bound = '1';
    cancelBtn.addEventListener('click', closeNewPlaylistModal);
  }
  if (createBtn && createBtn.dataset.bound !== '1') {
    createBtn.dataset.bound = '1';
    createBtn.addEventListener('click', createPlaylist);
  }
  if (nameInp && nameInp.dataset.bound !== '1') {
    nameInp.dataset.bound = '1';
    nameInp.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') createPlaylist();
    });
  }
}
